import { api } from "../scripts/api.js";

/*
   <app-class-boards> - the STUDENT's view of how the class is doing.

   Two boards behind one class picker:

     Active time   every classmate ranked by active minutes, this week and for
                   the term. GET /engagement/board.
     Scoreboard    per-assignment, whichever board that assignment carries -
                   per-question levels, or best elapsed time for a timed
                   challenge. GET /scoreboard, the same endpoint the professor
                   uses, which now answers a student with safe names only.

   NOTHING HERE IS ALWAYS ON. The class picker lists only the classes whose
   professor turned a board on (the two flags ride along on /me), and each tab
   only appears for a class that allows it. A student in a class with both
   switches off never sees this page at all - shell.js keeps the menu item out.

   NAMES. Every row is whatever the server sent, which for a student caller is
   the classmate's nickname or "Student 42". The client does not compose names
   and has no display_name or email to compose them from. Their own row is
   marked with the id the server flagged, not by matching on a name.
*/

const esc = (s) =>
   String(s ?? "").replace(/[&<>"']/g, (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
   );

// 93.4 -> "1h 33m", 42 -> "42m". Minutes, because that is the unit the whole
// engagement model is stated in (a 60 minute weekly floor).
function fmtMinutes(m) {
   const mins = Math.round(Number(m) || 0);
   if (mins < 60) return `${mins}m`;
   return `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, "0")}m`;
}

// 74.2 secs -> "1:14.2". Timed-challenge boards rank on elapsed seconds.
function fmtSecs(s) {
   if (s == null) return "—";
   const n = Number(s);
   if (!isFinite(n)) return "—";
   const m = Math.floor(n / 60);
   const rest = (n - m * 60).toFixed(1).padStart(4, "0");
   return m ? `${m}:${rest}` : `${n.toFixed(1)}s`;
}

class AppClassBoards extends HTMLElement {
   connectedCallback() {
      this.innerHTML = `
         <section id="class-boards">
            <header class="classes-header">
               <h1>Class standings</h1>
            </header>
            <p class="cb-intro">
               How the class is doing. Classmates show up by nickname, or as
               “Student 12” until they pick one. Set yours on
               <a href="#profile">My profile</a>.
            </p>

            <div class="cb-controls">
               <label class="cb-field">
                  <span>Class</span>
                  <select id="cb-class"></select>
               </label>
               <div class="cb-tabs" role="group" aria-label="Board">
                  <button type="button" class="cb-tab" data-tab="time">Active time</button>
                  <button type="button" class="cb-tab" data-tab="scores">Scoreboard</button>
               </div>
               <label class="cb-field" id="cb-assignment-field" hidden>
                  <span>Assignment</span>
                  <select id="cb-assignment"></select>
               </label>
            </div>

            <p class="msg" id="cb-msg"></p>
            <div id="cb-board"></div>
         </section>
      `;

      this.classSel = this.querySelector("#cb-class");
      this.asgField = this.querySelector("#cb-assignment-field");
      this.asgSel = this.querySelector("#cb-assignment");
      this.board = this.querySelector("#cb-board");
      this.msg = this.querySelector("#cb-msg");
      this.tab = "time";

      this.classSel.addEventListener("change", () => this.onClassChange());
      this.asgSel.addEventListener("change", () => this.loadScores());
      this.querySelectorAll(".cb-tab").forEach((b) =>
         b.addEventListener("click", () => this.setTab(b.dataset.tab))
      );
   }

   show() {
      if (this.loaded) return;
      this.loaded = true;
      this.init();
   }

   async init() {
      try {
         const res = await api("/me");
         const me = await res.json();
         if (!res.ok || !me.ok) throw new Error("me failed");
         this.myId = me.user_id;

         // Only classes this person is enrolled in AS A STUDENT and where at
         // least one board is switched on. A professor reaching this page (they
         // can, the route is not role-gated) sees the same thing their students
         // do, which is the point: it is the only way to check what you have
         // just exposed.
         this.classes = (me.classes || []).filter(
            (c) => c.students_see_scoreboard || c.students_see_engagement
         );

         if (!this.classes.length) {
            this.msg.textContent =
               "No class standings are open right now. Your professor decides whether to show them.";
            this.msg.className = "msg";
            this.querySelector(".cb-controls").hidden = true;
            return;
         }

         this.classSel.innerHTML = this.classes
            .map((c) => `<option value="${c.class_id}">${esc(`${c.course_code} · ${c.section}`)}</option>`)
            .join("");
         this.onClassChange();
      } catch {
         this.msg.textContent = "Couldn't load your classes.";
         this.msg.className = "msg err";
      }
   }

   get currentClass() {
      const id = Number(this.classSel.value);
      return this.classes.find((c) => c.class_id === id);
   }

   onClassChange() {
      const c = this.currentClass;
      if (!c) return;

      // Show only the tabs this class allows, and land on one that exists. A tab
      // that 403s is a bug report waiting to happen.
      const allowed = {
         time: !!c.students_see_engagement,
         scores: !!c.students_see_scoreboard,
      };
      this.querySelectorAll(".cb-tab").forEach((b) => {
         b.hidden = !allowed[b.dataset.tab];
      });
      if (!allowed[this.tab]) this.tab = allowed.time ? "time" : "scores";
      this.setTab(this.tab);
   }

   setTab(tab) {
      this.tab = tab;
      this.querySelectorAll(".cb-tab").forEach((b) =>
         b.classList.toggle("on", b.dataset.tab === tab)
      );
      this.board.innerHTML = "";
      this.msg.textContent = "";
      this.msg.className = "msg";
      if (tab === "time") {
         this.asgField.hidden = true;
         this.loadTime();
      } else {
         this.asgField.hidden = false;
         this.loadAssignments();
      }
   }

   /* ---- Active time ------------------------------------------------------ */

   async loadTime() {
      const classId = this.classSel.value;
      this.board.innerHTML = `<p class="empty">Loading...</p>`;
      try {
         const res = await api(`/engagement/board?class=${encodeURIComponent(classId)}`);
         const data = await res.json();
         if (!res.ok || !data.ok) throw new Error(data.reason || "failed");
         this.renderTime(data);
      } catch (err) {
         this.board.innerHTML = "";
         this.msg.textContent =
            String(err.message) === "not_authorized"
               ? "This board isn't open for this class."
               : "Couldn't load the time board.";
         this.msg.className = "msg err";
      }
   }

   renderTime(data) {
      const rows = data.students || [];
      if (!rows.length) {
         this.board.innerHTML = `<p class="empty">No active time recorded yet.</p>`;
         return;
      }
      const max = Math.max(1, ...rows.map((r) => r.total_minutes));

      /*
         THE SPLIT COLUMNS ARE THE WHOLE SAFEGUARD ON THIS BOARD.

         Self-logged minutes count toward the rank (David, 2026-09-01), which
         means a position here is partly self-reported. The board does not hide
         that: where a class has logging on, the term total is broken into
         measured and logged right beside the rank, so a row sitting at the top
         on mostly typed-in time is visible as such to the whole squad. That is a
         far better check than a rule nobody can see - and it is the reason the
         columns appear whenever the class HAS the feature on, not merely when
         someone has used it. A column that appears the moment the first person
         logs time would read as an accusation aimed at them.
      */
      const split = data.includes_logged_time === true;

      this.board.innerHTML = `
         <table class="cd-table cb-table">
            <thead>
               <tr><th>#</th><th>Who</th><th>This week</th><th>Term total</th>
                   ${split ? "<th>Measured</th><th>Logged</th>" : ""}<th></th></tr>
            </thead>
            <tbody>
               ${rows.map((r) => `
                  <tr class="${r.is_me ? "cb-me" : ""}">
                     <td class="cb-rank">${r.rank}</td>
                     <td>${esc(r.name)}${r.is_me ? ` <span class="cb-you">you</span>` : ""}</td>
                     <td>${fmtMinutes(r.week_minutes)}</td>
                     <td>${fmtMinutes(r.total_minutes)}</td>
                     ${split
                        ? `<td class="cb-sub">${fmtMinutes(r.total_tracked_minutes)}</td>
                           <td class="cb-sub">${fmtMinutes(r.total_logged_minutes)}</td>`
                        : ""}
                     <td class="cb-barcell">
                        <div class="cb-bar">
                           <div class="cb-bar-fill" style="width:${(r.total_minutes / max) * 100}%"></div>
                        </div>
                     </td>
                  </tr>`).join("")}
            </tbody>
         </table>
         <p class="cb-foot">Weeks run Monday to Sunday, ${esc(data.tz)}.${
            split
               ? " Totals include practice students logged themselves, shown separately."
               : ""
         }</p>`;
   }

   /* ---- Scoreboard ------------------------------------------------------- */

   async loadAssignments() {
      const classId = this.classSel.value;
      this.board.innerHTML = `<p class="empty">Loading...</p>`;
      try {
         // The student-scoped list (their own class assignments), not the
         // professor's /classes/:id, which they cannot read.
         const res = await api(`/me/classes/${encodeURIComponent(classId)}/assignments`);
         const data = await res.json();
         if (!res.ok || !data.ok) throw new Error(data.reason || "failed");
         const list = data.assignments || [];
         if (!list.length) {
            this.board.innerHTML = `<p class="empty">No assignments in this class yet.</p>`;
            this.asgSel.innerHTML = "";
            return;
         }
         this.asgList = list;
         this.asgSel.innerHTML = list
            .map((a) => `<option value="${a.assignment_id}">${esc(a.title || a.assignment_id)}</option>`)
            .join("");
         this.loadScores();
      } catch {
         this.board.innerHTML = "";
         this.msg.textContent = "Couldn't load the assignment list.";
         this.msg.className = "msg err";
      }
   }

   async loadScores() {
      const classId = this.classSel.value;
      const assignmentId = this.asgSel.value;
      if (!assignmentId) return;
      this.board.innerHTML = `<p class="empty">Loading...</p>`;
      /*
         A timed PACK (2026-10-14) has its own board: best completed timed run,
         penalties in, nickname only and never a grade. Same switch governs it
         (Class scoreboard), enforced by the server, not here.
      */
      const picked = (this.asgList || []).find((a) => String(a.assignment_id) === String(assignmentId));
      if (picked && picked.archetype === "timed_pack") return this.loadPackBoard(classId, assignmentId);
      try {
         const res = await api(
            `/scoreboard?assignment_id=${encodeURIComponent(assignmentId)}&class_id=${encodeURIComponent(classId)}`
         );
         const data = await res.json();
         if (!res.ok || !data.ok) throw new Error(data.reason || "failed");
         if (!data.configured) {
            this.board.innerHTML = `<p class="empty">No board for this assignment.</p>`;
            return;
         }
         if (data.mode === "timed") this.renderTimed(data);
         else this.renderQuestions(data);
      } catch (err) {
         this.board.innerHTML = "";
         this.msg.textContent =
            String(err.message) === "not_authorized"
               ? "This board isn't open for this class."
               : "Couldn't load that board.";
         this.msg.className = "msg err";
      }
   }

   async loadPackBoard(classId, packId) {
      try {
         const res = await api(`/packs/${encodeURIComponent(packId)}/board?class_id=${encodeURIComponent(classId)}`);
         const data = await res.json();
         if (!res.ok || !data.ok) throw new Error(data.reason || "failed");
         const rows = data.board || [];
         if (!rows.length) {
            this.board.innerHTML = `<p class="empty">Nobody has finished a timed run yet.</p>`;
            return;
         }
         this.board.innerHTML = `
            <table class="cd-table cb-table">
               <thead><tr><th>#</th><th>Who</th><th>Best time</th><th>Finished runs</th></tr></thead>
               <tbody>
                  ${rows.map((r) => {
                     const me = r.user_id === this.myId;
                     return `
                     <tr class="${me ? "cb-me" : ""}">
                        <td class="cb-rank">${r.rank}</td>
                        <td>${esc(r.nickname)}${me ? ` <span class="cb-you">you</span>` : ""}</td>
                        <td>${fmtSecs(r.best_total_secs)}</td>
                        <td>${esc(r.completed)}</td>
                     </tr>`;
                  }).join("")}
               </tbody>
            </table>
            <p class="cb-foot">Finished timed runs only. Times include any penalty seconds.</p>`;
      } catch (err) {
         this.board.innerHTML = "";
         this.msg.textContent =
            String(err.message) === "not_authorized"
               ? "This board isn't open for this class."
               : "Couldn't load that board.";
         this.msg.className = "msg err";
      }
   }

   renderTimed(data) {
      const rows = data.rows || [];
      if (!rows.length) {
         this.board.innerHTML = `<p class="empty">Nobody has run this one yet.</p>`;
         return;
      }
      this.board.innerHTML = `
         <table class="cd-table cb-table">
            <thead><tr><th>#</th><th>Who</th><th>Best time</th><th>Attempts</th></tr></thead>
            <tbody>
               ${rows.map((r, i) => {
                  const me = r.user_id === this.myId;
                  return `
                  <tr class="${me ? "cb-me" : ""}">
                     <td class="cb-rank">${i + 1}</td>
                     <td>${esc(r.nickname)}${me ? ` <span class="cb-you">you</span>` : ""}</td>
                     <td>${fmtSecs(r.reported_best_secs ?? r.observed_best_secs)}</td>
                     <td>${esc(r.reported_attempts ?? r.observed_attempts ?? "")}</td>
                  </tr>`;
               }).join("")}
            </tbody>
         </table>`;
   }

   renderQuestions(data) {
      const rows = data.rows || [];
      if (!rows.length) {
         this.board.innerHTML = `<p class="empty">No submissions yet.</p>`;
         return;
      }
      const levels = data.levels || [];
      const weighted = !!data.weighted;
      this.board.innerHTML = `
         <table class="cd-table cb-table">
            <thead>
               <tr>
                  <th>#</th><th>Who</th><th>${weighted ? "Points" : "Correct"}</th>
                  ${levels.map((l) => `<th>${esc(l.label ?? l.range ?? "")}</th>`).join("")}
               </tr>
            </thead>
            <tbody>
               ${rows.map((r, i) => {
                  const me = r.user_id === this.myId;
                  const headline = weighted
                     ? `${r.points_earned ?? 0} / ${r.points_total ?? 0}`
                     : `${r.correct_count ?? 0} / ${r.total_count ?? 0}`;
                  return `
                  <tr class="${me ? "cb-me" : ""}">
                     <td class="cb-rank">${i + 1}</td>
                     <td>${esc(r.name)}${me ? ` <span class="cb-you">you</span>` : ""}</td>
                     <td>${headline}</td>
                     ${(r.levels || []).map((pct) => `
                        <td class="cb-lvl">
                           <div class="cb-bar"><div class="cb-bar-fill" style="width:${pct}%"></div></div>
                        </td>`).join("")}
                  </tr>`;
               }).join("")}
            </tbody>
         </table>`;
   }
}

customElements.define("app-class-boards", AppClassBoards);
